import type { ElementType } from 'react';
import type { MotionProps } from 'framer-motion';
import type { BoxProps } from '@mui/material/Box';

import { motion as fm } from 'framer-motion';

import Box from '@mui/material/Box';

import { motion as motionTokens } from '../../theme/motion';

// ----------------------------------------------------------------------

// Created once at module scope (motion.create per-render would remount children).
const MotionBox = fm.create(Box);

// Built from the named BoxProps + MotionProps (framer overrides the conflicting
// DOM/pan handlers, so omit those Box keys) rather than `ComponentProps<typeof
// MotionBox>`, whose inferred type isn't portably nameable in the emitted .d.ts
// (TS2742). `component` is re-declared because the non-polymorphic BoxProps
// default drops Box's polymorphic `component` prop.
type MotionBoxProps = Omit<BoxProps, keyof MotionProps> & MotionProps & { component?: ElementType };

export type FadeInProps = Omit<MotionBoxProps, 'initial' | 'animate'> & {
  /** Upward offset (px) the element rises from. Default 8. */
  offsetY?: number;
  /** Initial scale; use ~0.98 for cards/dialogs. Default 1 (no scale). */
  scaleFrom?: number;
  /** Entrance delay in seconds. Default 0. */
  delay?: number;
};

/**
 * Mount-entrance wrapper: fade + small rise (+ optional subtle scale). Entrances
 * use `ease.out` per emil taste. Reduced motion is honored globally via
 * `<MotionConfig reducedMotion="user">`, which drops `y`/`scale` and keeps the
 * opacity fade. Accepts all `Box` props (`sx`, `component`, etc.).
 */
export function FadeIn({ offsetY = 8, scaleFrom = 1, delay = 0, children, ...rest }: FadeInProps) {
  return (
    <MotionBox
      initial={{ opacity: 0, y: offsetY, scale: scaleFrom, filter: 'blur(4px)' }}
      animate={{ opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }}
      transition={{
        duration: motionTokens.durationMs.base / 1000,
        ease: motionTokens.easeArray.out,
        delay,
      }}
      {...rest}
    >
      {children}
    </MotionBox>
  );
}
